import { Box, Stack, Text } from '@mantine/core'
import { every, find, get, isEmpty, map, some } from 'lodash'
import React, { ReactElement, useMemo } from 'react'

import Alert from '../../../../../1stlog-ui/src/components/Alert'
import Card from '../../../../../1stlog-ui/src/components/Card'
import {
  FieldPrefix,
  Label,
  useField,
} from '../../../../../1stlog-ui/src/components/Field'
import useFieldValue from '../../../../../1stlog-ui/src/components/Field/useFieldValue'
import FieldArray from '../../../../../1stlog-ui/src/components/FieldArray'
import { LANGUAGE_OPTIONS } from '../../../../../1stlog-ui/src/components/LanguageSelect'
import { Notification } from './Notification'

type ClientNotificationsProps = {}

export const ClientNotifications = (
  props: ClientNotificationsProps,
): ReactElement => {
  const {} = props

  const isPickup = useField('isPickup')
  const addressField = useMemo(
    () => `${isPickup ? 'pickup' : 'delivery'}.address`,
    [isPickup],
  )
  const emailField = `${addressField}.mail`
  const phoneField = `${addressField}.phone1`

  const phone = useField(phoneField, { onlyValid: true })
  const email = useField(emailField, { onlyValid: true })

  const clientNotifications = useField('clientNotifications')
  const clientNotificationsLanguage = useFieldValue(
    'clientNotificationsLanguage',
  )
  const clientNotificationsDisabled = useFieldValue(
    'clientNotificationsDisabled',
  )
  const clientNotificationsAdditionalPhones = useFieldValue<
    { phone: string }[]
  >('clientNotificationsAdditionalPhones')
  const clientNotificationsAdditionalEmails = useFieldValue<
    { email: string }[]
  >('clientNotificationsAdditionalEmails')
  const showPhoneWarning = useMemo(() => {
    return (
      some(clientNotifications, (n) => n.isEnabled && Boolean(n.sms)) && !phone
    )
  }, [clientNotifications, phone])

  const showEmailWarning = useMemo(() => {
    return (
      some(clientNotifications, (n) => n.isEnabled && Boolean(n.email)) &&
      !email
    )
  }, [clientNotifications, email])

  const showPhone = useMemo(
    () => some(clientNotifications, (n) => n.isEnabled && n.phone),
    [clientNotifications],
  )

  const showEmail = useMemo(
    () => some(clientNotifications, (n) => n.isEnabled && n.email),
    [clientNotifications],
  )

  if (
    isEmpty(clientNotifications) ||
    every(clientNotifications, (s) => s.isEnabled === false)
  ) {
    return <></>
  }

  return (
    <>
      <Card title={i18n.t('client_notifications', 'Kundenbenachrichtigungen')}>
        {clientNotificationsDisabled ? (
          <div>
            <Alert info>
              {i18n.t(
                'no_client_notifications_desired',
                'Keine Kundenbenachrichtigungen gewünscht',
              )}
            </Alert>
          </div>
        ) : (
          <div>
            {showEmail && (
              <Box>
                <Label>
                  {i18n.t(
                    'client_notifications_send_to_mail',
                    'Per E-Mail an:',
                  )}
                </Label>
                <Stack spacing={2}>
                  {map(
                    [{ email }, ...clientNotificationsAdditionalEmails].filter(
                      (s) => s?.email,
                    ),
                    ({ email }) => (
                      <Text key={email}>{email}</Text>
                    ),
                  )}
                </Stack>
              </Box>
            )}
            {showPhone && (
              <Box>
                <Label>
                  {i18n.t(
                    'client_notifications_send_to_phone',
                    'Per E-Mail an:',
                  )}
                </Label>
                <Stack spacing={2}>
                  {map(
                    [{ phone }, ...clientNotificationsAdditionalPhones].filter(
                      (s) => s?.phone,
                    ),
                    ({ phone }) => (
                      <Text key={phone}>{phone}</Text>
                    ),
                  )}
                </Stack>
              </Box>
            )}
            <Box mt="md">
              <FieldArray name="clientNotifications">
                {({ fields }) => (
                  <Stack spacing={5}>
                    {fields.map((name) => (
                      <FieldPrefix key={name} prefix={name}>
                        <Notification />
                      </FieldPrefix>
                    ))}
                  </Stack>
                )}
              </FieldArray>
            </Box>
            <Text mt="md">
              {i18n.t('client_notifications_will_be_in_language', {
                defaultValue:
                  'Kundenbenachrichtigungen werden in {{language}} versendet.',
                language: get(
                  find(LANGUAGE_OPTIONS, {
                    value: clientNotificationsLanguage,
                  }),
                  'label',
                  clientNotificationsLanguage,
                ),
              })}
            </Text>
          </div>
        )}
      </Card>
    </>
  )
}
